import { Coffee, Fingerprint, LogIn, LogOut, Play, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import { useFeature } from "@/app/session/features";
import { useBreak, useClock, useToday, type Today } from "@/features/attendance/api";
import {
  breakLeftMinutes,
  breakMinutes,
  breakOverrun,
  breakUsedMinutes,
  breakUsedSeconds,
  DEFAULT_BREAK_ALLOWANCE,
  workedSeconds,
} from "@/features/attendance/lib/breaks";
import { attendanceErrorMessage } from "@/features/attendance/lib/errors";
import { useNow } from "@/features/time/hooks/useNow";
import { useLocale, useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";
import { elapsedSeconds, formatClock } from "@/shared/lib/duration";
import { formatTime } from "@/shared/lib/format";

interface ClockChipProps {
  /** The phone's top bar: the icon, the running clock and the action only. */
  compact?: boolean;
  className?: string;
}

function useClockAction(today: Today | undefined) {
  const t = useT();
  const { locale } = useLocale();
  const clock = useClock();
  const run = () => {
    const action = today?.open_session ? "out" : "in";
    clock.mutate(action, {
      onSuccess: (session) => {
        if (action === "in" && session.location_mismatch) {
          toast.warning(t("attendance.clock.mismatchToast"));
          return;
        }
        toast.success(
          t(action === "in" ? "attendance.clock.inToast" : "attendance.clock.outToast", {
            start: formatTime(
              action === "in" ? session.started_at : (session.ended_at ?? session.started_at),
              locale,
            ),
          }),
        );
      },
      onError: (error) => toast.error(t(attendanceErrorMessage(error))),
    });
  };
  return { run, pending: clock.isPending };
}

function useBreakAction(today: Today | undefined) {
  const t = useT();
  const { locale } = useLocale();
  const pause = useBreak();
  const run = () => {
    if (!today) return;
    const action = today.open_break ? "end" : "start";
    const allowance = today.break_allowance_minutes ?? DEFAULT_BREAK_ALLOWANCE;
    const used = breakUsedMinutes(today, Date.now());
    const left = breakLeftMinutes(used, allowance);
    pause.mutate(action, {
      onSuccess: (entry) => {
        const start = formatTime(entry.started_at, locale);
        if (action === "start") {
          // A second break carries on from the first: say what is already used and what is left.
          toast.success(
            used > 0
              ? t("attendance.break.resumeToast", { start, used, left })
              : t("attendance.break.startToast", { start }),
          );
          return;
        }
        const took = breakMinutes(entry, Date.now());
        if (breakOverrun(used, allowance)) {
          // Gently: past the allowance is for HR to look at, never a deduction.
          toast.info(t("attendance.break.overToast", { took, used, total: allowance }));
        } else {
          toast.success(t("attendance.break.endToast", { took, used, total: allowance, left }));
        }
      },
      onError: (error) => toast.error(t(attendanceErrorMessage(error))),
    });
  };
  return { run, pending: pause.isPending };
}

type ClockState = "idle" | "working" | "break";

const TONE: Record<ClockState, { tile: string; text: string; dot: string; card: string }> = {
  idle: {
    tile: "bg-surface-sunken text-ink-subtle ring-line",
    text: "text-ink",
    dot: "bg-ink-faint",
    card: "border-line",
  },
  working: {
    tile: "bg-success-soft text-success ring-success/20",
    text: "text-success",
    dot: "bg-success",
    card: "border-success/25 bg-linear-to-b from-success-soft/60 to-surface to-60%",
  },
  break: {
    tile: "bg-info-soft text-info ring-info/20",
    text: "text-info",
    dot: "bg-info",
    card: "border-info/25 bg-linear-to-b from-info-soft/70 to-surface to-60%",
  },
};

/**
 * Attendance in the app shell, beside the task timer and clearly apart from it: one tap clocks
 * in (office or remote, as the day's plan says) or out, and the running time shows meanwhile.
 * While clocked in, a break starts and ends here too, with its own timer and the day's break
 * time against the allowance. Nothing shows for people whose attendance is not tracked.
 *
 * The sidebar gets a card (status, a large clock, the day's break meter and two clear actions);
 * the phone's top bar keeps a compact pill.
 */
export function ClockChip({ compact = false, className }: ClockChipProps) {
  const t = useT();
  const { locale } = useLocale();
  const peopleOn = useFeature("people");
  const today = useToday(peopleOn);
  const data = today.data;
  const open = data?.open_session ?? null;
  const onBreak = open ? (data?.open_break ?? null) : null;
  const now = useNow(open !== null);
  const action = useClockAction(data);
  const pause = useBreakAction(data);
  if (!peopleOn || !data?.attendance_required) return null;

  const state: ClockState = onBreak ? "break" : open ? "working" : "idle";
  const tone = TONE[state];
  const running = onBreak ?? open;
  // On a regular break the clock shows all of today's breaks, so a second break picks up where
  // the first stopped; at work it shows today's work less the breaks, standing still meanwhile.
  const seconds = onBreak
    ? onBreak.kind === "regular"
      ? breakUsedSeconds(data, now)
      : elapsedSeconds(onBreak.started_at, now)
    : workedSeconds(data, now);
  const elapsed = running ? formatClock(seconds, locale) : null;
  const since = running ? formatTime(running.started_at, locale) : "";
  const timerCaption = t(onBreak ? "attendance.break.totalToday" : "attendance.clock.workedToday");
  const mismatch = open?.location_mismatch ?? false;
  const actionLabel = t(open ? "attendance.clock.out" : "attendance.clock.in");
  const ActionIcon = open ? LogOut : LogIn;
  const allowance = data.break_allowance_minutes ?? DEFAULT_BREAK_ALLOWANCE;
  const used = breakUsedMinutes(data, now);
  const over = breakOverrun(used, allowance);
  const usedText = t(over ? "attendance.break.over" : "attendance.break.used", {
    used,
    total: allowance,
  });
  const leftText = over
    ? ""
    : t("attendance.break.left", { left: breakLeftMinutes(used, allowance) });
  const breakLabel = t(onBreak ? "attendance.break.end" : "attendance.break.start");
  const StateIcon = onBreak ? Coffee : Fingerprint;

  const timer = elapsed ? (
    <span
      role="timer"
      className="shrink-0"
      aria-label={`${timerCaption} · ${t(onBreak ? "attendance.break.since" : "attendance.clock.since", { start: since })}`}
    >
      <span
        aria-hidden="true"
        dir="ltr"
        className={cn(
          "font-semibold tabular-nums tracking-tight",
          compact ? "text-[13px]" : "text-[28px] leading-none",
          tone.text,
        )}
      >
        {elapsed}
      </span>
    </span>
  ) : null;

  const mismatchFlag = mismatch ? (
    <span title={t("attendance.clock.mismatch")} className="text-warning">
      <TriangleAlert aria-hidden="true" className="size-3.5" />
      <span className="sr-only">{t("attendance.clock.mismatch")}</span>
    </span>
  ) : null;

  if (compact) {
    return (
      <div
        role="group"
        aria-label={t("attendance.clock.label")}
        className={cn(
          "flex h-8 max-w-full min-w-0 items-center gap-1.5 rounded-full border bg-surface ps-2 pe-1 shadow-xs",
          tone.card,
          className,
        )}
      >
        <StateIcon aria-hidden="true" className={cn("size-4 shrink-0", tone.text)} />
        {timer}
        {onBreak || (open && over) ? (
          <span className="sr-only">{`${usedText} ${leftText}`}</span>
        ) : null}
        {mismatchFlag}
        {open ? (
          <button
            type="button"
            onClick={pause.run}
            disabled={pause.pending}
            aria-busy={pause.pending || undefined}
            aria-label={breakLabel}
            title={breakLabel}
            className={cn(
              "ms-auto grid size-6 shrink-0 place-items-center rounded-full transition-colors disabled:opacity-60",
              onBreak
                ? "bg-info text-ink-inverse hover:bg-info/85"
                : "bg-surface-sunken text-ink hover:bg-info-soft hover:text-info",
            )}
          >
            {onBreak ? (
              <Play aria-hidden="true" className="size-3.5 rtl:-scale-x-100" />
            ) : (
              <Coffee aria-hidden="true" className="size-3.5" />
            )}
          </button>
        ) : null}
        <button
          type="button"
          onClick={action.run}
          disabled={action.pending}
          aria-busy={action.pending || undefined}
          aria-label={actionLabel}
          title={actionLabel}
          className={cn(
            "grid size-6 shrink-0 place-items-center rounded-full transition-colors disabled:opacity-60",
            !open && "ms-auto",
            open
              ? "bg-surface-sunken text-ink hover:bg-danger-soft hover:text-danger"
              : "bg-ink text-ink-inverse hover:bg-ink/85",
          )}
        >
          <ActionIcon aria-hidden="true" className="size-3.5 rtl:-scale-x-100" />
        </button>
      </div>
    );
  }

  const title = t(
    onBreak ? "attendance.break.onBreak" : open ? "attendance.clock.on" : "attendance.clock.title",
  );
  const subtitle = onBreak
    ? t("attendance.break.thisOne", { start: since })
    : open
      ? t("attendance.clock.sinceShort", {
          mode: t(`attendance.clock.plan.${open.mode}`),
          start: since,
        })
      : t(`attendance.clock.plan.${data.planned}`);
  const meter = Math.min(used / Math.max(allowance, 1), 1);

  return (
    <section
      role="group"
      aria-label={t("attendance.clock.label")}
      className={cn(
        "relative overflow-hidden rounded-2xl border bg-surface p-3 shadow-xs transition-[border-color,background-color] duration-300",
        tone.card,
        className,
      )}
    >
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className={cn(
            "relative grid size-9 shrink-0 place-items-center rounded-xl ring-1 transition-colors duration-300",
            tone.tile,
          )}
        >
          <StateIcon key={state} className="size-[18px] animate-pop-in" />
          {running ? (
            <span className="absolute -end-0.5 -top-0.5 flex size-2.5">
              <span
                className={cn(
                  "absolute inline-flex size-full animate-ping rounded-full opacity-50 motion-reduce:hidden",
                  tone.dot,
                )}
              />
              <span
                className={cn(
                  "relative inline-flex size-2.5 rounded-full ring-2 ring-surface",
                  tone.dot,
                )}
              />
            </span>
          ) : null}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13.5px] leading-tight font-semibold text-ink">{title}</p>
          <p className="mt-0.5 flex min-w-0 items-center gap-1 text-[12px] leading-tight text-ink-subtle">
            <span className="truncate">{subtitle}</span>
            {mismatchFlag}
          </p>
        </div>
      </div>

      {timer ? (
        <div className="mt-3 flex flex-col gap-1">
          <span aria-hidden="true" className="text-[11.5px] leading-tight text-ink-subtle">
            {timerCaption}
          </span>
          {timer}
        </div>
      ) : null}

      {open ? (
        <div className="mt-3">
          <div className="mb-1.5 flex items-baseline justify-between gap-2 text-[11.5px]">
            {over ? null : (
              <span className="flex shrink-0 items-center gap-1 whitespace-nowrap text-ink-subtle">
                <Coffee aria-hidden="true" className="size-3" />
                {t("attendance.break.today")}
              </span>
            )}
            <span
              title={over ? t("attendance.break.overHint") : undefined}
              className={cn(
                "min-w-0 truncate font-medium tabular-nums",
                over ? "text-warning" : onBreak ? "text-info" : "text-ink-muted",
              )}
            >
              {usedText}
              {over ? (
                <span className="sr-only">{` ${t("attendance.break.overHint")}`}</span>
              ) : null}
            </span>
          </div>
          <div aria-hidden="true" className="h-1.5 overflow-hidden rounded-full bg-surface-sunken">
            <div
              className={cn(
                "h-full rounded-full transition-[width] duration-700 ease-out",
                over ? "bg-warning" : "bg-info",
              )}
              style={{ width: `${Math.max(meter * 100, used > 0 ? 4 : 0)}%` }}
            />
          </div>
          {leftText ? (
            <p
              className={cn(
                "mt-1 truncate text-[11.5px] tabular-nums",
                onBreak ? "font-medium text-info" : "text-ink-subtle",
              )}
            >
              {leftText}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className={cn("mt-3 grid gap-1.5", open ? "grid-cols-2" : "grid-cols-1")}>
        {open ? (
          <button
            type="button"
            onClick={pause.run}
            disabled={pause.pending}
            aria-busy={pause.pending || undefined}
            aria-label={breakLabel}
            title={onBreak ? breakLabel : `${breakLabel} · ${usedText} ${leftText}`}
            className={cn(
              "flex h-8 min-w-0 items-center justify-center gap-1.5 rounded-lg px-2 text-[12.5px] font-semibold transition-[background-color,color,box-shadow] disabled:opacity-60",
              onBreak
                ? "bg-info text-ink-inverse shadow-xs hover:bg-info/85"
                : "bg-surface text-ink ring-1 ring-line hover:bg-info-soft hover:text-info hover:ring-info/30",
            )}
          >
            {onBreak ? (
              <Play aria-hidden="true" className="size-3.5 shrink-0 rtl:-scale-x-100" />
            ) : (
              <Coffee aria-hidden="true" className="size-3.5 shrink-0" />
            )}
            <span className="truncate">
              {t(onBreak ? "attendance.break.endShort" : "attendance.break.startShort")}
            </span>
          </button>
        ) : null}
        <button
          type="button"
          onClick={action.run}
          disabled={action.pending}
          aria-busy={action.pending || undefined}
          aria-label={actionLabel}
          title={actionLabel}
          className={cn(
            "flex h-8 min-w-0 items-center justify-center gap-1.5 rounded-lg px-2 text-[12.5px] font-semibold transition-[background-color,color,box-shadow] disabled:opacity-60",
            open
              ? "bg-surface text-ink ring-1 ring-line hover:bg-danger-soft hover:text-danger hover:ring-danger/30"
              : "bg-ink text-ink-inverse shadow-xs hover:bg-ink/85",
          )}
        >
          <ActionIcon aria-hidden="true" className="size-3.5 shrink-0 rtl:-scale-x-100" />
          <span className="truncate">
            {t(open ? "attendance.clock.outShort" : "attendance.clock.in")}
          </span>
        </button>
      </div>
    </section>
  );
}
